import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

export default function Layout({ children, fullWidth = false }) {
  const { user, companies, switchCompany, logout, viewingAs, exitViewAsClient } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const location = useLocation();
  const isAdmin = user?.role === 'admin' && !viewingAs;
  const isAdminTeam = (user?.role === 'admin' || user?.role === 'standard') && !viewingAs;
  const hasMultipleCompanies = companies.length > 1;
  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setCompanyDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const navItems = isAdminTeam
    ? (isAdmin
      ? [
          { path: '/admin', label: 'Dashboard' },
          { path: '/admin/clients', label: 'Clients' },
          { path: '/settings/team', label: 'Team' },
          { path: '/settings', label: 'Settings' },
        ]
      : [
          { path: '/admin', label: 'Dashboard' },
        ])
    : [
        { path: '/', label: 'Employees' },
        { path: '/new-starter', label: 'New Starter' },
        { path: '/leaver', label: 'Leaver' },
        { path: '/documents', label: 'Documents' },
        { path: '/history', label: 'History' },
        { path: '/activity', label: 'Activity' },
      ];

  // The Settings item owns every /settings/* route except the Team sub-page,
  // which has its own top-nav entry.
  const isItemActive = (item) =>
    item.path === '/settings'
      ? location.pathname.startsWith('/settings') && location.pathname !== '/settings/team'
      : location.pathname === item.path;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 font-body transition-colors">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-white focus:text-brand-700 focus:rounded focus:shadow-lg focus:text-sm focus:font-medium">
        Skip to main content
      </a>
      <nav className="bg-brand-600 dark:bg-gray-800 shadow-lg">
        <div className={fullWidth ? 'w-full px-4 sm:px-6 lg:px-8' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'}>
          <div className="flex justify-between h-20">
            <div className="flex items-center">
              <Link to={isAdminTeam ? '/admin' : '/'} className="flex items-center">
                <img
                  src={darkMode ? '/logo-white - full.png' : '/Logo in black.png'}
                  alt="Startup Accountancy"
                  className="h-16"
                />
              </Link>
              <div className="hidden sm:flex sm:ml-8 sm:space-x-1 sm:items-center">
                {navItems.map(item => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`px-4 py-2 rounded-md text-base font-medium transition-colors ${
                      isItemActive(item)
                        ? 'text-brand-900 bg-accent'
                        : 'text-white hover:bg-white hover:bg-opacity-15'
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {/* Dark mode toggle */}
              <button
                onClick={toggleDarkMode}
                className="p-2 rounded-full bg-white bg-opacity-20 text-white hover:bg-opacity-30 transition-colors"
                title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {darkMode ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                )}
              </button>

              {/* Company name / switcher */}
              {hasMultipleCompanies ? (
                <div className="relative hidden sm:block" ref={dropdownRef}>
                  <button
                    onClick={() => setCompanyDropdownOpen(!companyDropdownOpen)}
                    className="flex items-center space-x-1 text-brand-100 text-base hover:text-white transition-colors"
                  >
                    <span>{viewingAs?.company_name || user?.company_name}</span>
                    <svg className={`w-4 h-4 transition-transform ${companyDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {companyDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-700 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                      <div className="py-1">
                        {companies.map(c => (
                          <button
                            key={c.id}
                            onClick={() => {
                              if (c.id !== user?.company_id) {
                                switchCompany(c.id);
                              }
                              setCompanyDropdownOpen(false);
                            }}
                            className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                              c.id === user?.company_id
                                ? 'bg-brand-50 dark:bg-brand-900 text-brand-700 dark:text-brand-300 font-medium'
                                : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600'
                            }`}
                          >
                            {c.company_name}
                            {c.id === user?.company_id && (
                              <span className="ml-2 text-xs text-brand-500">Current</span>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <span className="text-brand-100 text-base hidden sm:block">
                  {viewingAs?.company_name || user?.company_name || user?.name}
                </span>
              )}

              <Link
                to="/security"
                className="hidden sm:flex items-center p-2 rounded-full bg-white bg-opacity-20 text-white hover:bg-opacity-30 transition-colors"
                title="Security Settings"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </Link>

              <button
                onClick={() => { if (window.confirm('Are you sure you want to log out?')) logout(); }}
                className="hidden sm:block bg-white bg-opacity-20 text-white px-4 py-2 rounded text-base font-medium hover:bg-opacity-30 transition-colors"
              >
                Logout
              </button>

              {/* Hamburger menu button - mobile only */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="sm:hidden p-2 rounded-md bg-white bg-opacity-20 text-white hover:bg-opacity-30 transition-colors"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
        {/* Mobile nav - collapsible */}
        {mobileMenuOpen && (
          <div className="sm:hidden px-4 pb-3 space-y-1 border-t border-white border-opacity-20">
            {navItems.map(item => (
              <Link
                key={item.path}
                to={item.path}
                className={`block px-3 py-3 rounded-md text-base font-medium transition-colors ${
                  isItemActive(item)
                    ? 'text-brand-900 bg-accent'
                    : 'text-white hover:bg-white hover:bg-opacity-15'
                }`}
              >
                {item.label}
              </Link>
            ))}
            {hasMultipleCompanies && (
              <div className="pt-2 border-t border-white border-opacity-20">
                <p className="text-brand-200 text-xs mb-1 px-3">Switch Company</p>
                {companies.map(c => (
                  <button
                    key={c.id}
                    onClick={() => c.id !== user?.company_id && switchCompany(c.id)}
                    className={`block w-full text-left px-3 py-3 rounded-md text-sm font-medium transition-colors ${
                      c.id === user?.company_id
                        ? 'text-brand-900 bg-accent'
                        : 'text-white hover:bg-white hover:bg-opacity-15'
                    }`}
                  >
                    {c.company_name}
                  </button>
                ))}
              </div>
            )}
            <div className="pt-2 border-t border-white border-opacity-20">
              <Link
                to="/security"
                className="block px-3 py-3 rounded-md text-base font-medium text-white hover:bg-white hover:bg-opacity-15 transition-colors"
              >
                Security Settings
              </Link>
              <button
                onClick={() => { if (window.confirm('Are you sure you want to log out?')) logout(); }}
                className="block w-full text-left px-3 py-3 rounded-md text-base font-medium text-white hover:bg-white hover:bg-opacity-15 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        )}
      </nav>
      {viewingAs && (
        <div className="bg-purple-600 text-white text-center py-2 px-4 text-sm font-medium">
          Viewing as <strong>{viewingAs.company_name}</strong>
          <button onClick={exitViewAsClient} className="ml-3 px-3 py-0.5 bg-white text-purple-700 rounded text-xs font-semibold hover:bg-purple-50">
            Exit
          </button>
        </div>
      )}
      <main id="main-content" className={fullWidth ? '' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'}>
        {children}
      </main>
      <footer className="border-t border-gray-200 dark:border-gray-700 py-4 text-center">
        <p className="text-sm text-gray-400 dark:text-gray-500">
          Need help? Email{' '}
          <a href="mailto:payroll@startupaccountancy.com" className="text-brand-500 hover:text-brand-600 dark:text-brand-400">
            payroll@startupaccountancy.com
          </a>
        </p>
      </footer>
    </div>
  );
}
